import React, { useContext, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '@/App';
import { APP_VERSION } from '@/config/version';
import { 
  Mic, CreditCard, Share2, MessageSquare, Target, 
  ShieldCheck, FileText, Smartphone, Mail, Info, ChevronRight,
  Sparkles, ExternalLink
} from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { token } = useContext(AuthContext);
  const location = useLocation();

  // Forzar scroll en cambio de ruta para páginas públicas
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);

  return (
    <footer className="bg-stone-900 text-stone-400 border-t border-stone-800 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-12">
          {/* Company Info */}
          <div className="md:col-span-4 flex flex-col items-start">
            <Link 
              to="/"
              className="inline-flex items-center gap-2.5 mb-4 group cursor-pointer"
            >
              <img 
                src="/icon-192x192.png" 
                alt="PGastos" 
                className="h-9 w-auto group-hover:scale-105 active:scale-95 transition-transform duration-150 ease-spring"
              />
              <span className="text-xl font-black text-white tracking-tight">PGastos</span>
            </Link>
            <p className="text-sm text-stone-400 mb-6 leading-relaxed max-w-sm font-medium">
              Control de gastos personal sin fricción. Simple, directo y enfocado en darte claridad financiera total.
            </p>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-stone-800/80 border border-stone-700/60 text-stone-300 text-xs font-semibold">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              <span>100% privado · Sin contraseñas bancarias</span>
            </div>
          </div>

          {/* Product Links */}
          <div className="md:col-span-3">
            <h3 className="text-xs font-bold text-stone-200 uppercase tracking-widest mb-4">
              Producto
            </h3>
            <ul className="space-y-2.5 text-sm font-medium">
              <li>
                <Link 
                  to={!token ? "/?pricing=true" : "/pricing"}
                  className="group inline-flex items-center gap-1.5 text-stone-400 hover:text-white transition-all duration-150 ease-spring hover:translate-x-1"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-stone-600 group-hover:text-indigo-400 transition-colors" />
                  <span>Planes y Precios</span>
                </Link>
              </li>
              <li>
                <Link 
                  to={token ? '/transactions' : '/voz-info'}
                  className="group inline-flex items-center gap-1.5 text-stone-400 hover:text-white transition-all duration-150 ease-spring hover:translate-x-1"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-stone-600 group-hover:text-indigo-400 transition-colors" />
                  <span>Dictado por Voz</span>
                </Link>
              </li>
              <li>
                <Link 
                  to={token ? '/accounts' : '/tarjetas-msi-info'}
                  className="group inline-flex items-center gap-1.5 text-stone-400 hover:text-white transition-all duration-150 ease-spring hover:translate-x-1"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-stone-600 group-hover:text-indigo-400 transition-colors" />
                  <span>Tarjetas y MSI</span>
                </Link>
              </li>
              <li>
                <Link 
                  to={token ? '/debts' : '/deudas-info'}
                  className="group inline-flex items-center gap-1.5 text-stone-400 hover:text-white transition-all duration-150 ease-spring hover:translate-x-1"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-stone-600 group-hover:text-indigo-400 transition-colors" />
                  <span>Deudas y Cobros SPEI</span>
                </Link>
              </li>
              <li>
                <Link 
                  to={token ? '/ai-insights' : '/ai-financiera-info'}
                  className="group inline-flex items-center gap-1.5 text-stone-400 hover:text-white transition-all duration-150 ease-spring hover:translate-x-1"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-stone-600 group-hover:text-indigo-400 transition-colors" />
                  <span>IA Financiera y Chat</span>
                </Link>
              </li>
              <li>
                <Link 
                  to={token ? '/goals' : '/objetivos-info'}
                  className="group inline-flex items-center gap-1.5 text-stone-400 hover:text-white transition-all duration-150 ease-spring hover:translate-x-1"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-stone-600 group-hover:text-indigo-400 transition-colors" />
                  <span>Objetivos Financieros</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Company Links */}
          <div className="md:col-span-2">
            <h3 className="text-xs font-bold text-stone-200 uppercase tracking-widest mb-4">
              Empresa
            </h3>
            <ul className="space-y-2.5 text-sm font-medium">
              <li>
                <Link 
                  to="/about"
                  className="group inline-flex items-center gap-1.5 text-stone-400 hover:text-white transition-all duration-150 ease-spring hover:translate-x-1"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-stone-600 group-hover:text-indigo-400 transition-colors" />
                  <span>Acerca de</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/contact"
                  className="group inline-flex items-center gap-1.5 text-stone-400 hover:text-white transition-all duration-150 ease-spring hover:translate-x-1"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-stone-600 group-hover:text-indigo-400 transition-colors" />
                  <span>Contacto</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/install-pwa"
                  className="group inline-flex items-center gap-1.5 text-stone-400 hover:text-white transition-all duration-150 ease-spring hover:translate-x-1"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-stone-600 group-hover:text-indigo-400 transition-colors" />
                  <span>Instalar como app</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div className="md:col-span-3">
            <h3 className="text-xs font-bold text-stone-200 uppercase tracking-widest mb-4">
              Legal
            </h3>
            <ul className="space-y-2.5 text-sm font-medium">
              <li>
                <Link 
                  to="/privacy"
                  className="group inline-flex items-center gap-1.5 text-stone-400 hover:text-white transition-all duration-150 ease-spring hover:translate-x-1"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-stone-600 group-hover:text-indigo-400 transition-colors" />
                  <span>Política de Privacidad</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/terms"
                  className="group inline-flex items-center gap-1.5 text-stone-400 hover:text-white transition-all duration-150 ease-spring hover:translate-x-1"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-stone-600 group-hover:text-indigo-400 transition-colors" />
                  <span>Términos y Condiciones</span>
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-stone-800 pt-8 mt-12 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-stone-500 font-medium">
          <p>© {currentYear} VJRDEV Company. Todos los derechos reservados.</p>
          <div className="flex items-center gap-3">
            <span className="px-2 py-0.5 rounded-md bg-stone-800 text-stone-400 font-mono text-[11px] border border-stone-700/60">
              v{APP_VERSION}
            </span>
            <a 
              href="https://vjrodriguez.dev" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-stone-300 transition-colors duration-150 inline-flex items-center gap-1"
            >
              <span>Desarrollado por VJRDEV</span>
              <ExternalLink className="w-3 h-3 text-stone-600" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

